'use client';

import { useEffect, useState, type RefObject } from 'react';

import {
  getVoiceComposerBounds,
  observeVoiceComposerGeometry,
} from '@/lib/floating-voice-composer-clearance';

type Rect = { left: number; top: number; width: number; height: number };

/** Gap kept between the tray and the bottom edge or a composer below it. */
export const FOLDER_UPLOADS_TRAY_GAP_PX = 16;

/** The tray never rises so far that less than this much of it shows. */
const MIN_TRAY_ROOM_PX = 160;
/** A composer this close to the viewport bottom is docked there. */
const DOCKED_COMPOSER_PX = 64;

/**
 * How far above the viewport bottom the upload tray sits (#5823 TASTE): it
 * never covers a chat composer's send area, so it rises above a composer
 * docked at the bottom in the tray's own zone (same column, lower edge
 * inside the band the tray would occupy). A composer higher up the page
 * (the /design home), scrolling with it, or off-screen does not move it.
 */
export function folderUploadsTrayBottom(args: {
  viewport: { width: number; height: number };
  trayWidth: number;
  trayHeight: number;
  composers: ReadonlyArray<Rect>;
}): number {
  const right = args.viewport.width - FOLDER_UPLOADS_TRAY_GAP_PX;
  const left = right - args.trayWidth;
  const zoneTop =
    args.viewport.height - FOLDER_UPLOADS_TRAY_GAP_PX - args.trayHeight;
  const lifted = args.composers.reduce((bottom, composer) => {
    const composerBottom = composer.top + composer.height;
    if (
      composer.width <= 0 ||
      composer.height <= 0 ||
      composer.left >= right ||
      composer.left + composer.width <= left ||
      composer.top >= args.viewport.height ||
      composerBottom <= zoneTop ||
      composerBottom <= 0 ||
      // Only a composer docked at the bottom: an in-flow one scrolling up
      // with the page (mobile /design) must not drag the tray along.
      composerBottom < args.viewport.height - DOCKED_COMPOSER_PX
    )
      return bottom;
    return Math.max(
      bottom,
      args.viewport.height - composer.top + FOLDER_UPLOADS_TRAY_GAP_PX / 2
    );
  }, FOLDER_UPLOADS_TRAY_GAP_PX);
  // Always leave room for the tray on screen.
  return Math.min(
    lifted,
    Math.max(
      FOLDER_UPLOADS_TRAY_GAP_PX,
      args.viewport.height - MIN_TRAY_ROOM_PX
    )
  );
}

/**
 * The tray's full content height, not the height its own lift clamps
 * (`maxHeight` follows `bottom`), so the band never depends on the lift and
 * the tray cannot bounce between lifted and not (review S1).
 */
export function folderUploadsTrayContentHeight(
  scrollHeight: number,
  viewportHeight: number
): number {
  return Math.max(
    0,
    Math.min(scrollHeight, viewportHeight - 2 * FOLDER_UPLOADS_TRAY_GAP_PX)
  );
}

/**
 * The tray's bottom offset, kept current as composers mount and grow and as
 * the tray itself grows (a card expands, another upload starts).
 */
export function useFolderUploadsTrayBottom(
  trayRef: RefObject<HTMLElement | null>,
  enabled: boolean
): number {
  const [bottom, setBottom] = useState(FOLDER_UPLOADS_TRAY_GAP_PX);
  useEffect(() => {
    if (!enabled) return;
    const update = () => {
      const tray = trayRef.current;
      setBottom(
        folderUploadsTrayBottom({
          viewport: { width: window.innerWidth, height: window.innerHeight },
          trayWidth: tray?.offsetWidth ?? 0,
          trayHeight: folderUploadsTrayContentHeight(
            tray?.scrollHeight ?? 0,
            window.innerHeight
          ),
          composers: getVoiceComposerBounds(),
        })
      );
    };
    update();
    const stop = observeVoiceComposerGeometry(update);
    window.addEventListener('resize', update);
    // The tray's cards change its content height; watch each of them.
    const tray = trayRef.current;
    const sizes = new ResizeObserver(update);
    const watchCards = () => {
      sizes.disconnect();
      if (!tray) return;
      sizes.observe(tray);
      // The observer's first delivery measures; no extra update needed.
      for (const card of Array.from(tray.children)) sizes.observe(card);
    };
    const cards = new MutationObserver(watchCards);
    if (tray) cards.observe(tray, { childList: true });
    watchCards();
    return () => {
      stop();
      sizes.disconnect();
      cards.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [enabled, trayRef]);
  return bottom;
}
